'use client';

import { StaticTextContext } from '@/components/context/StaticTextProvider';
import { Feedback } from '@/components/ui/data-display/feedback/Feedback';
import { GridCol } from '@/components/ui/layouts/grids/GridCol';
import { GridRow } from '@/components/ui/layouts/grids/GridRow';
import { Section } from '@/components/ui/layouts/sections/Section';
import { request } from '@/services/server/request';
import { Button, Form, Icon } from '@mage-ui/components';
import { cx } from '@mage-ui/styled-system/css';
import { Flex } from '@mage-ui/styled-system/jsx';
import {
  flexWrapper,
  gridCol,
  icon,
  inputFileBadge,
  inputFileRoot,
  inputRoot,
  textStyles,
} from '@mage-ui/styled-system/recipes';
import { useState } from 'react';
import { useContext } from 'react';
import { AlertItem } from '../../feedback/alert-item/AlertItem';
import { TopicEnum } from './enums';
import { rules } from './rules';

export const ContactForm = ({ locale }) => {
  const { st } = useContext(StaticTextContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editoraErrors, setEditoraErrors] = useState({});
  const [contact, setContact] = useState();

  const setError = (field, error) => {
    setEditoraErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setEditoraErrors({});

    handleResponse(
      await request('/api/send-contact', 'POST', {
        ...values,
      }),
    );

    setIsSubmitting(false);
  };

  const _externalErrors = Object.fromEntries(
    Object.entries(editoraErrors).map(([field, error]) => [
      field,
      {
        type: error.type ?? 'server',
        message: Array.isArray(error.message)
          ? error.message.join(', ')
          : error.message,
      },
    ]),
  );

  const handleResponse = ({ status, data }) => {
    switch (status) {
      case 200:
      case 201:
      case 204:
        setContact(data?.data?.body?.contact || data?.body?.contact);
        break;

      case 422:
      case 400:
      case 401:
      case 403:
      case 404:
      case 500:
        if (status === 422) {
          handleValidationError(data?.data);
        } else {
          handleServerError(data?.data);
        }
        break;

      default:
        break;
    }

    setIsSubmitting(false);
  };

  const handleValidationError = (data) => {
    const { errors } = data;

    for (const field of Object.keys(errors)) {
      setError(field, {
        type: 'server',
        message: errors?.[field],
      });
    }
    setError('root.serverError', {
      message: data?.message ?? st('validation.server_error'),
    });
  };

  const handleServerError = (data) => {
    setError('root.serverError', {
      message: data?.details ?? st('validation.server_error'),
    });
  };

  const ContactFormFeedback = () => (
    <Feedback
      language={locale}
      data={{
        fields: {
          title: st('forms.contact.feedback.title'),
          intro: st('forms.contact.feedback.subtitle'),
          content: '',
          cta_text: st('forms.contact.feedback.cta_text'),
        },
      }}
    />
  );

  return (
    <Section>
      {editoraErrors['root.serverError']?.message && (
        <AlertItem text={editoraErrors['root.serverError']?.message} />
      )}

      {contact ? (
        <ContactFormFeedback />
      ) : (
        <Form
          onSubmit={onSubmit}
          defaultValues={{ accept_privacy: false }}
          rules={rules({ st })}
        >
          <Flex direction='column' gap='24'>
            <GridRow>
              <GridCol
                className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
              >
                <Form.InputText
                  name='name'
                  label={st('forms.contact.name.label')}
                  withAsterisk={true}
                />
              </GridCol>

              <GridCol
                className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
              >
                <Form.InputText
                  name='phone'
                  label={st('forms.contact.phone.label')}
                  withAsterisk={true}
                />
              </GridCol>
            </GridRow>

            <GridRow>
              <GridCol
                className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
              >
                <Form.InputText
                  name='document_number'
                  label={st('forms.contact.document_number.label')}
                  withAsterisk={true}
                />
              </GridCol>

              <GridCol
                className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
              >
                <Form.InputFile
                  name='document_file'
                  label={st('forms.contact.document_file.label')}
                  placeholder={st('forms.contact.document_file.placeholder')}
                  multiple={false}
                  classNames={{
                    root: cx(inputRoot(), inputFileRoot()),
                  }}
                  badgeClassName={inputFileBadge()}
                />
              </GridCol>
            </GridRow>

            <GridRow>
              <GridCol
                className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
              >
                <Form.InputText
                  name='email'
                  label={st('forms.contact.email.label')}
                  type='email'
                  withAsterisk={true}
                />
              </GridCol>

              <GridCol
                className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
              >
                <Form.InputText
                  name='email_confirmation'
                  label={st('forms.contact.email_confirmation.label')}
                  type='email'
                  withAsterisk={true}
                />
              </GridCol>
            </GridRow>

            <GridRow>
              <GridCol
                className={cx(gridCol({ colSpanM: '12', colSpanL: '12' }))}
              >
                <Form.Select
                  name='topic'
                  label={st('forms.contact.topic.label')}
                  placeholder={st('forms.contact.topic.placeholder')}
                  options={Object.values(TopicEnum).map((topic) => ({
                    id: topic,
                    value: st(`forms.contact.topic.${topic}.label`),
                  }))}
                  withAsterisk={true}
                  endSlot={
                    <Icon name='unfold_more' classNames={icon({ size: 'm' })} />
                  }
                />
              </GridCol>
            </GridRow>

            <GridRow>
              <GridCol
                className={cx(gridCol({ colSpanM: '12', colSpanL: '12' }))}
              >
                <Form.Textarea
                  name='message'
                  label={st('forms.contact.message.label')}
                  withAsterisk={true}
                  autosize={true}
                  minRows={3}
                />
              </GridCol>
            </GridRow>

            <GridRow>
              <GridCol
                className={cx(gridCol({ colSpanM: '9', colSpanL: '9' }))}
              >
                <Form.Checkbox
                  name='accept_privacy'
                  label={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: st('forms.contact.accept_privacy.label'),
                      }}
                    />
                  }
                  required={true}
                />
              </GridCol>

              <GridCol
                className={cx(gridCol({ colSpanM: '3', colSpanL: '3' }))}
              >
                <div className={flexWrapper({ justifyM: 'end' })}>
                  <Button type='submit' loading={isSubmitting}>
                    {st('forms.contact.submit.label')}
                  </Button>
                </div>
              </GridCol>
            </GridRow>
          </Flex>
          {Array.from({ length: 6 }).map((_, idx) => {
            const i = idx + 1;
            const title = st(`forms.contact.info_below${i}.title`);
            const desc = st(`forms.contact.info_below${i}.description`);
            const html = `<strong>${title}</strong>: ${desc}`;

            return (
              <div
                key={`info-below-${i}`}
                className={cx(textStyles())}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          })}
        </Form>
      )}
    </Section>
  );
};
